import React, { useState, useEffect, useContext, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { graphql } from '@apollo/client/react/hoc'
import { useQuery } from '@apollo/client'
import gql from "graphql-tag"
import { Container, Row, Col, Button } from 'reactstrap'
import './TaskManagementContainer.css'
import TaskManagementListHeader from './TaskManagementListHeader'
import TaskList from './TaskList'
import MyTasksGroupedList from './MyTasksGroupedList'
import TaskKanbanView from './TaskKanbanView'
import { AllBoardsQuery, BoardQuery } from './Queries'
import withApolloClient from '../withApolloClient'
import { CurrentUserContext } from '../Context/CurrentUserContext'
import BoardActionsMenu from './TaskComponents/BoardActionsMenu'
import ManagePreferenceHOC from '../Material/ManagePreferenceHOC'
import { TASK_TABLE_HEADERS as taskTableHeaders } from '../Constants'
import { getTaskPreferenceId } from '../utils'
import { UserQuery } from '../Functions/querys'

const UpdateMemberMutation = gql`
    mutation updateMember($input: UpdateMemberMutationInput!) {
        updateMember(input: $input) {
            ok
            errors {
                messages
                field
            }
        }
    }
`

function TaskManagementContainer({ allBoards, loadMoreEntries, apolloClient, columnConfig, memberTablePreference, refetchPreferences, currentUser: currentUserFromHOC }) {
    const { boardId } = useParams()
    const { currentUser: currentUserFromContext } = useContext(CurrentUserContext)
    const currentUser = currentUserFromHOC || currentUserFromContext

    // We read member preferences directly via useQuery instead of relying on the currentUser prop.
    // currentUser is passed down from Content.js which runs its own UserQuery HOC — but the member
    // object it carries may be a partial cache write (missing preference fields like boardViewTypeConfig)
    // because Apollo merges fields from multiple queries into the same normalized cache entry, and not
    // all queries request the same fields. Using useQuery with cache-first here reads the full member
    // data from the cache that Content.js already populated — no extra network request.
    const { data: userData, loading: userPrefsLoading } = useQuery(UserQuery, { fetchPolicy: 'cache-first' })
    const memberPrefs = userData?.currentUser?.member

    // BoardQuery moved here from TaskList so we can merge columnConfig (from ManagePreferenceHOC)
    // with board.columnSet (custom column names/data) in one place via useMemo.
    const { data: boardData, refetch: refetchBoard } = useQuery(BoardQuery, {
        variables: { id: boardId },
        skip: !boardId,
        fetchPolicy: 'cache-and-network',
    })
    const board = boardData?.board
    const boards = allBoards?.edges || []
    const loadedBoardCount = boards.length + 1
    const totalBoardCount = (allBoards?.pageInfo?.length || boards.length) + 1
    const activeBoardTitle = boardId && boardId !== "tasks" ? boards.find(({ node }) => node.id === boardId)?.node?.title || board?.title : "My Tasks"
    const hasSavedPreferences = Array.isArray(memberTablePreference) && memberTablePreference.length > 0
    const visiblePreferenceIds = useMemo(
        () => new Set(Array.isArray(memberTablePreference) ? memberTablePreference : []),
        [memberTablePreference]
    )

    // Build mergedColumnConfig: standard columns from HOC + custom columns from board, in saved order.
    // When board hasn't loaded (no boardId), strip any stale custom column IDs from saved preferences.
    const mergedColumnConfig = useMemo(() => {
        const base = columnConfig && columnConfig.length > 0 ? columnConfig : taskTableHeaders

        // No board (My Tasks view) — return only standard columns
        if (!board) return base.filter(c => !c.id.startsWith('custom_column_'))

        // Build a map of live custom columns from board.columnSet
        const liveCustomMap = new Map(
            (board.columnSet?.edges || [])
                .filter(e => !e.node.isArchived)
                .map(e => [`custom_column_${e.node.pk}`, e.node])
        )

        // Update saved entries: remove deleted custom columns, enrich live ones with name/columnNode
        const updated = base
            .filter(c => !c.id.startsWith('custom_column_') || liveCustomMap.has(c.id))
            .map(c => {
                const liveNode = liveCustomMap.get(c.id)
                return liveNode ? { ...c, name: liveNode.title, columnNode: liveNode, isCustom: true } : c
            })

        // Append new custom columns not yet in saved preferences
        const savedIds = new Set(base.map(c => c.id))
        const newCustom = Array.from(liveCustomMap.entries())
            .filter(([id]) => !savedIds.has(id))
            .map(([id, node]) => ({
                id,
                name: node.title,
                visible: hasSavedPreferences ? visiblePreferenceIds.has(id) : true,
                showInPicker: true,
                positionIsFixed: false,
                isCustom: true,
                columnNode: node,
            }))

        return [...updated, ...newCustom]
    }, [columnConfig, board, hasSavedPreferences, visiblePreferenceIds])
    const [orderBy, setOrderBy] = useState(['dueDate'])
    const [groupBy, setGroupBy] = useState('status')

    // Optimistic local state for view type — set immediately on toggle so the UI switches
    // instantly without waiting for the UserQuery refetch to complete after saving.
    // Reset to null when navigating to a different board so the persisted preference takes over.
    const [pendingViewType, setPendingViewType] = useState(null)

    // Derives the view type for the current board from the persisted member preferences.
    // Falls back to the legacy global taskManagementViewType, then to "list".
    const getPersistedViewType = () => {
        try {
            const config = JSON.parse(memberPrefs?.boardViewTypeConfig || '{}')
            const boardKey = boardId || "tasks"
            return config[boardKey] || memberPrefs?.taskManagementViewType || "list"
        } catch (e) {
            return memberPrefs?.taskManagementViewType || "list"
        }
    }

    // Use the optimistic value while the user is on the same board, otherwise use persisted value.
    const viewType = pendingViewType !== null ? pendingViewType : getPersistedViewType()

    useEffect(() => {
        // Clear the optimistic value when the board changes so the correct persisted
        // preference for the new board is shown immediately.
        setPendingViewType(null)
    }, [boardId])

    useEffect(() => {
        if (!memberPrefs?.kanbanColumnConfig) return
        try {
            const config = JSON.parse(memberPrefs.kanbanColumnConfig)
            setGroupBy(boardId && config[boardId] ? config[boardId] : 'status')
        } catch (e) { }
    }, [memberPrefs?.kanbanColumnConfig, boardId])

    const handleGroupByChange = async (newGroupBy) => {
        setGroupBy(newGroupBy)
        if (!boardId) return
        try {
            let existing = {}
            if (memberPrefs?.kanbanColumnConfig) {
                try { existing = JSON.parse(memberPrefs.kanbanColumnConfig) } catch (e) { }
            }
            await apolloClient.mutate({
                mutation: UpdateMemberMutation,
                variables: { input: { memberId: memberPrefs?.id, kanbanColumnConfig: JSON.stringify({ ...existing, [boardId]: newGroupBy }) } },
                refetchQueries: ['UserQuery'],
            })
        } catch (e) { }
    }

    const handleViewTypeChange = async (newViewType) => {
        // Set optimistically so the UI switches instantly before the network request completes.
        setPendingViewType(newViewType)
        try {
            let existing = {}
            if (memberPrefs?.boardViewTypeConfig) {
                try { existing = JSON.parse(memberPrefs.boardViewTypeConfig) } catch (e) { }
            }
            const boardKey = boardId || "tasks"
            await apolloClient.mutate({
                mutation: UpdateMemberMutation,
                variables: {
                    input: {
                        memberId: memberPrefs?.id,
                        boardViewTypeConfig: JSON.stringify({ ...existing, [boardKey]: newViewType }),
                    }
                },
                refetchQueries: ["UserQuery"],
            })
        } catch (error) {
            console.error("Error saving view type:", error)
        }
    }
    const [filterValues, setFilterValues] = useState({ searchTerm: '', isArchived: false })

    useEffect(() => {
        if (!boardId)
            setFilterValues({ ...filterValues, statuses: ["OPEN", "IN_PROGRESS"] })
    }, [boardId])

    useEffect(() => {
        if (viewType !== "kanban" || groupBy !== "status") return
        setFilterValues((currentFilters) => {
            if (!currentFilters?.statuses) return currentFilters
            const { statuses, ...remainingFilters } = currentFilters
            return remainingFilters
        })
    }, [viewType, groupBy])

    const updateOrderBy = (string) => {
        let order = [...orderBy]
        if (!order.includes(string) && !order.includes("-" + string)) {
            order = [string]
        } else {
            let index = order.indexOf(string)
            if (order.indexOf(string) > -1) {
                order[index] = "-" + string
            } else {
                index = order.indexOf("-" + string)
                index > -1 && (order[index] = string)
            }
        }
        setOrderBy(order)
    }

    return (
        <div className="TaskManagementContainer">
            <Container fluid>
                <TaskManagementListHeader activeTab={!boardId || boardId === 'tasks' ? "myTasks" : "boardTasks"} filterValues={filterValues} setFilterValues={(res) => setFilterValues({ ...res })} showNewBoardButton viewType={viewType} onViewTypeChange={handleViewTypeChange} groupBy={groupBy} onGroupByChange={handleGroupByChange} columnConfig={mergedColumnConfig} refetchPreferences={refetchPreferences} currentUser={currentUser} preferenceType={getTaskPreferenceId(boardId)} />
                <Row>
                    <Col xs="2" className="TaskBoardColumn">
                        <aside className="task-board-sidebar">
                            <div className="task-board-sidebar-header">
                                <div>
                                    <div className="task-board-sidebar-eyebrow">Workspace</div>
                                    <h4 className="task-board-sidebar-title">Boards</h4>
                                </div>
                                <span className="task-board-sidebar-count">{loadedBoardCount}</span>
                            </div>
                            <div className="task-board-sidebar-meta">
                                Showing {loadedBoardCount} of {totalBoardCount} boards
                            </div>
                            <div className="task-board-sidebar-current">
                                <span className="task-board-sidebar-current-label">Viewing</span>
                                <span className="task-board-sidebar-current-value">{activeBoardTitle}</span>
                            </div>
                            <ul className="TaskBoardList">
                                <li className={!boardId || boardId === 'tasks' ? 'active' : ''}>
                                    <div className="board-sidebar-item">
                                        <Link className="board-sidebar-link" to="/boards/tasks/">
                                            <span className="board-sidebar-icon"><i className="fa fa-inbox" aria-hidden="true" /></span>
                                            <span className="board-sidebar-content">
                                                <span className="board-sidebar-title-text">My Tasks</span>
                                                <span className="board-sidebar-subtitle">Personal task queue</span>
                                            </span>
                                        </Link>
                                    </div>
                                </li>
                                {boards.map((boardEdge) => (
                                    <li className={boardId === boardEdge.node.id ? 'active' : ''} key={boardEdge.node.id}>
                                        <div className="board-sidebar-item">
                                            <Link className="board-sidebar-link" to={`/boards/${boardEdge.node.id}/tasks/`}>
                                                <span className="board-sidebar-icon"><i className="fa fa-columns" aria-hidden="true" /></span>
                                                <span className="board-sidebar-content">
                                                    <span className="board-sidebar-title-text">{boardEdge.node.title}</span>
                                                    <span className="board-sidebar-subtitle">
                                                        {boardEdge.node.description || `Board #${boardEdge.node.pk}`}
                                                    </span>
                                                </span>
                                            </Link>
                                            <span className="board-edit-icon board-actions-wrapper" onClick={(e) => e.stopPropagation()}>
                                                <BoardActionsMenu boardId={boardEdge.node.id} />
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            {allBoards?.pageInfo?.hasNextPage && (
                                <button type="button" className="task-board-load-more" onClick={loadMoreEntries}>
                                    <span>Load more boards</span>
                                    <i className="fa fa-arrow-down" aria-hidden="true" />
                                </button>
                            )}
                        </aside>
                    </Col>
                    <Col xs="10" className="TaskListColumn">
                        {userPrefsLoading && !memberPrefs ? (
                            <div className="text-center mt-5"><i className="fa fa-spinner fa-spin fa-2x" /></div>
                        ) : viewType === "kanban" ? (
                            boardId ?
                                <TaskKanbanView boardId={boardId} {...filterValues} orderBy={orderBy} groupBy={groupBy} /> :
                                <TaskKanbanView assignedToIds={[currentUser.id]} {...filterValues} orderBy={orderBy} groupBy={groupBy} />
                        ) : (
                            boardId ?
                                <TaskList boardId={boardId} {...filterValues} orderBy={orderBy} updateOrderBy={updateOrderBy} columnConfig={mergedColumnConfig} board={board} refetchBoardQuery={refetchBoard} /> :
                                <MyTasksGroupedList currentUserId={currentUser.id} filterValues={filterValues} orderBy={orderBy} updateOrderBy={updateOrderBy} columnConfig={mergedColumnConfig} />
                        )}
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

export default graphql(AllBoardsQuery, {
    options: ({ searchTerm }) => ({
        variables: {
            searchTerm,
            orderBy: ["date_added"],
            isArchived: false,
            first: 10
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network'
    }),
    props: ({ data: { loading, allBoards, fetchMore, searchTerm, variables, refetch } }) => (
        {
            loading,
            allBoards,
            variables,
            refetch,
            refetchDriversQuery: () => {
                //No apollo function that refetches in place with pagination considered so this function instead
                let currentLength = allBoards && allBoards.edges ? allBoards.edges.length : 1
                return fetchMore({
                    query: AllBoardsQuery,
                    variables: {
                        ...variables,
                        first: currentLength,
                    },
                    updateQuery: (previousResult, { fetchMoreResult }) => {
                        const newEdges = fetchMoreResult.allBoards.edges
                        const pageInfo = fetchMoreResult.allBoards.pageInfo
                        return {
                            allBoards: {
                                edges: [...newEdges],
                                pageInfo,
                                searchTerm: previousResult.searchTerm,
                                __typename: previousResult.allBoards.__typename
                            },
                        }
                    },
                })
            },
            loadMoreEntries: () => {
                return fetchMore({
                    query: AllBoardsQuery,
                    variables: {
                        cursor: allBoards.pageInfo.endCursor,
                        ...variables
                    },
                    updateQuery: (previousResult, { fetchMoreResult }) => {
                        const newEdges = fetchMoreResult.allBoards.edges
                        const pageInfo = fetchMoreResult.allBoards.pageInfo

                        return {
                            // Put the new comments at the end of the list and update `pageInfo`
                            // so we have the new `endCursor` and `hasNextPage` values
                            allBoards: {
                                edges: [...previousResult.allBoards.edges, ...newEdges],
                                pageInfo,
                                searchTerm: previousResult.searchTerm,
                                __typename: previousResult.allBoards.__typename
                            },
                        }
                    },
                })
            },
        }
    )
})(withApolloClient(ManagePreferenceHOC(
    (props) => getTaskPreferenceId(props.params?.boardId),
    taskTableHeaders
)(TaskManagementContainer)))
